/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { UnstyledButton } from '@/shared/ui';

export interface PageHeaderViewToggleItem<T extends string> {
  value: T;
  label: string;
  icon: React.ReactNode;
  testId: string;
}

interface PageHeaderViewToggleProps<T extends string> {
  value: T;
  onChange: (next: T) => void;
  items: PageHeaderViewToggleItem<T>[];
  ariaLabel: string;
  testId: string;
}

/**
 * A view switch (list/grid, list/summary) that sits in the search row.
 *
 * The shared `SegmentedControl` pill is a grey tray with smaller type, which
 * reads as a different control next to Filtros and Agrupar. This one wears
 * their outline, padding, type and radius, and marks the chosen segment with
 * the same `--selection-active` / `--seed-base` pair an active Agrupar shows.
 */
export function PageHeaderViewToggle<T extends string>({ value, onChange, items, ariaLabel, testId }: PageHeaderViewToggleProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      data-testid={testId}
      style={{
        display: 'inline-flex',
        border: '1px solid var(--border-medium)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
      }}
    >
      {items.map((item, index) => {
        const active = item.value === value;
        return (
          <UnstyledButton
            key={item.value}
            type="button"
            role="tab"
            aria-selected={active}
            data-testid={item.testId}
            onClick={() => onChange(item.value)}
            className="focus-visible:ring-inset"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              padding: 'var(--space-2) var(--space-3)',
              fontSize: 'var(--text-body-sm)',
              fontWeight: 'var(--font-medium)' as unknown as number,
              fontFamily: 'var(--font-family)',
              whiteSpace: 'nowrap',
              cursor: 'pointer',
              background: active ? 'var(--selection-active)' : 'transparent',
              color: active ? 'var(--seed-base)' : 'var(--text-secondary)',
              borderLeft: index > 0 ? '1px solid var(--border-medium)' : 'none',
              transition: 'var(--transition-standard)',
            }}
          >
            {item.icon}
            {item.label}
          </UnstyledButton>
        );
      })}
    </div>
  );
}
